import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiTag, FiPercent, FiCalendar, FiShoppingCart, FiUsers, FiX, FiPackage, FiSearch, FiGrid, FiCreditCard, FiEdit2, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import apiClient from '../utils/apiClient';
import { useToast } from '../context/ToastContext';
import '../styles/Admin.css';
import '../styles/AdminCoupons.css';

// Helper to get today's date in YYYY-MM-DD format
const getToday = () => new Date().toISOString().split('T')[0];
const getNextMonth = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

// Available categories
const CATEGORIES = [
  { id: 'stationary', label: 'Stationary' },
  { id: 'textiles', label: 'Textiles & Fabrics' },
  { id: 'pottery', label: 'Pottery & Ceramics' },
  { id: 'handicrafts', label: 'Handicrafts' },
  { id: 'electronics', label: 'Electronics' },
  { id: 'home-decor', label: 'Home Decor' }
];

// Available banks for bank offers
const BANKS = [
  'All Banks',
  'HDFC Bank',
  'ICICI Bank',
  'State Bank of India',
  'Axis Bank',
  'Kotak Mahindra Bank',
  'Yes Bank',
  'IndusInd Bank',
  'IDFC First Bank',
  'RBL Bank',
  'American Express',
  'Citibank',
  'Standard Chartered'
];

const CARD_TYPES = [
  'All Cards',
  'Credit Card',
  'Debit Card'
];

const AdminCoupons = () => {
  const toast = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState('coupons'); // 'coupons' or 'bankOffers'

  // Coupon states
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderValue: '',
    validFrom: getToday(),
    validUntil: getNextMonth(),
    usageLimit: '',
    applicableType: 'all',
    applicableCategories: [],
    applicableProductIds: [],
    selectedProducts: []
  });

  // Bank Offer states
  const [bankOffers, setBankOffers] = useState([]);
  const [bankOffersLoading, setBankOffersLoading] = useState(true);
  const [showBankOfferForm, setShowBankOfferForm] = useState(false);
  const [editingBankOffer, setEditingBankOffer] = useState(null);
  const [bankOfferFormData, setBankOfferFormData] = useState({
    title: '',
    description: '',
    bankName: 'All Banks',
    cardType: 'All Cards',
    discountType: 'percentage',
    discountValue: '',
    maxDiscount: '',
    minOrderValue: '',
    applicableBinRanges: '',
    paymentMethodCode: '',
    validFrom: getToday(),
    validUntil: getNextMonth(),
    termsAndConditions: '',
    displayOrder: 0
  });

  useEffect(() => {
    if (activeTab === 'coupons') {
      fetchCoupons();
    } else {
      fetchBankOffers();
    }
  }, [activeTab]);

  // Debounced product search
  useEffect(() => {
    if (formData.applicableType !== 'products' || !productSearch.trim()) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await apiClient.get(`/products?search=${encodeURIComponent(productSearch)}&limit=10`);
        setSearchResults(response.data.products || []);
      } catch (error) {
        console.error('Error searching products:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearch, formData.applicableType]);

  // ==================== COUPON FUNCTIONS ====================
  const fetchCoupons = async () => {
    try {
      const token = sessionStorage.getItem('adminToken');
      const response = await apiClient.get('/coupons', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discountType: 'percentage',
      discountValue: '',
      minOrderValue: '',
      validFrom: getToday(),
      validUntil: getNextMonth(),
      usageLimit: '',
      applicableType: 'all',
      applicableCategories: [],
      applicableProductIds: [],
      selectedProducts: []
    });
    setProductSearch('');
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.applicableType === 'categories' && formData.applicableCategories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }
    if (formData.applicableType === 'products' && formData.applicableProductIds.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    try {
      const token = sessionStorage.getItem('adminToken');
      const submitData = {
        ...formData,
        selectedProducts: undefined
      };
      await apiClient.post('/coupons', submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowForm(false);
      fetchCoupons();
      resetForm();
      toast.success('Coupon created successfully!');
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast.error(error.response?.data?.error || 'Failed to create coupon');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const token = sessionStorage.getItem('adminToken');
      await apiClient.delete(`/coupons/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCoupons();
      toast.success('Coupon deleted');
    } catch (error) {
      console.error('Error deleting coupon:', error);
    }
  };

  const toggleCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      applicableCategories: prev.applicableCategories.includes(categoryId)
        ? prev.applicableCategories.filter(c => c !== categoryId)
        : [...prev.applicableCategories, categoryId]
    }));
  };

  const addProduct = (product) => {
    if (formData.applicableProductIds.includes(product.id)) return;
    setFormData(prev => ({
      ...prev,
      applicableProductIds: [...prev.applicableProductIds, product.id],
      selectedProducts: [...prev.selectedProducts, { id: product.id, name: product.name }]
    }));
    setProductSearch('');
    setSearchResults([]);
  };

  const removeProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      applicableProductIds: prev.applicableProductIds.filter(id => id !== productId),
      selectedProducts: prev.selectedProducts.filter(p => p.id !== productId)
    }));
  };

  const getApplicabilityLabel = (coupon) => {
    const type = coupon.applicableType || 'all';
    if (type === 'all') return 'All Products';
    if (type === 'categories') {
      const cats = coupon.applicableCategories || [];
      return cats.length > 2 ? `${cats.length} Categories` : cats.join(', ');
    }
    if (type === 'products') {
      const prods = coupon.applicableProductIds || [];
      return `${prods.length} Product${prods.length !== 1 ? 's' : ''}`;
    }
    return 'All Products';
  };

  // ==================== BANK OFFER FUNCTIONS ====================
  const fetchBankOffers = async () => {
    try {
      setBankOffersLoading(true);
      const token = sessionStorage.getItem('adminToken');
      console.log('Fetching bank offers with token:', !!token);
      const response = await apiClient.get('/bank-offers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Bank offers response:', response.data);
      setBankOffers(response.data || []);
    } catch (error) {
      console.error('Error fetching bank offers:', error);
      toast.error('Failed to load bank offers');
    } finally {
      setBankOffersLoading(false);
    }
  };

  const resetBankOfferForm = () => {
    setBankOfferFormData({
      title: '',
      description: '',
      bankName: 'All Banks',
      cardType: 'All Cards',
      discountType: 'percentage',
      discountValue: '',
      maxDiscount: '',
      minOrderValue: '',
      applicableBinRanges: '',
      paymentMethodCode: '',
      validFrom: getToday(),
      validUntil: getNextMonth(),
      termsAndConditions: '',
      displayOrder: 0
    });
    setEditingBankOffer(null);
  };

  const handleBankOfferSubmit = async (e) => {
    e.preventDefault();

    if (!bankOfferFormData.title || !bankOfferFormData.description || !bankOfferFormData.discountValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = sessionStorage.getItem('adminToken');
      const submitData = {
        ...bankOfferFormData,
        applicableBinRanges: bankOfferFormData.applicableBinRanges
          ? bankOfferFormData.applicableBinRanges.split(',').map(s => s.trim()).filter(Boolean)
          : []
      };

      if (editingBankOffer) {
        await apiClient.put(`/bank-offers/${editingBankOffer.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bank offer updated successfully!');
      } else {
        await apiClient.post('/bank-offers', submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bank offer created successfully!');
      }

      setShowBankOfferForm(false);
      fetchBankOffers();
      resetBankOfferForm();
    } catch (error) {
      console.error('Error saving bank offer:', error);
      toast.error(error.response?.data?.error || 'Failed to save bank offer');
    }
  };

  const handleEditBankOffer = (offer) => {
    setBankOfferFormData({
      title: offer.title || '',
      description: offer.description || '',
      bankName: offer.bankName || 'All Banks',
      cardType: offer.cardType || 'All Cards',
      discountType: offer.discountType || 'percentage',
      discountValue: offer.discountValue?.toString() || '',
      maxDiscount: offer.maxDiscount?.toString() || '',
      minOrderValue: offer.minOrderValue?.toString() || '',
      applicableBinRanges: offer.applicableBinRanges?.join(', ') || '',
      paymentMethodCode: offer.paymentMethodCode || '',
      validFrom: offer.validFrom?.split('T')[0] || getToday(),
      validUntil: offer.validUntil?.split('T')[0] || getNextMonth(),
      termsAndConditions: offer.termsAndConditions || '',
      displayOrder: offer.displayOrder || 0
    });
    setEditingBankOffer(offer);
    setShowBankOfferForm(true);
  };

  const handleToggleBankOffer = async (offerId) => {
    try {
      const token = sessionStorage.getItem('adminToken');
      await apiClient.patch(`/bank-offers/${offerId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBankOffers();
      toast.success('Offer status updated');
    } catch (error) {
      console.error('Error toggling bank offer:', error);
      toast.error('Failed to update offer status');
    }
  };

  const handleDeleteBankOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this bank offer?')) return;

    try {
      const token = sessionStorage.getItem('adminToken');
      await apiClient.delete(`/bank-offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBankOffers();
      toast.success('Bank offer deleted');
    } catch (error) {
      console.error('Error deleting bank offer:', error);
      toast.error('Failed to delete bank offer');
    }
  };

  return (
    <div className="admin-coupons-page">
      <div className="coupons-header">
        <div>
          <h1><FiTag /> Offers & Coupons</h1>
          <p>Manage discount coupons and bank offers for your customers</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="offers-tabs">
        <button
          className={`tab-btn ${activeTab === 'coupons' ? 'active' : ''}`}
          onClick={() => setActiveTab('coupons')}
        >
          <FiTag /> Coupon Codes
        </button>
        <button
          className={`tab-btn ${activeTab === 'bankOffers' ? 'active' : ''}`}
          onClick={() => setActiveTab('bankOffers')}
        >
          <FiCreditCard /> Bank Offers
        </button>
      </div>

      {/* COUPONS TAB */}
      {activeTab === 'coupons' && (
        <>
          <div className="section-header">
            <h2>Coupon Codes</h2>
            <button className="create-coupon-btn" onClick={() => setShowForm(!showForm)}>
              {showForm ? <><FiX /> Cancel</> : <><FiPlus /> Create Coupon</>}
            </button>
          </div>

          {showForm && (
            <div className="coupon-form-card">
              <h3>Create New Coupon</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* Coupon Code */}
                  <div className="form-field full-width">
                    <label><FiTag /> Coupon Code</label>
                    <input
                      type="text"
                      placeholder="Enter code (e.g. SAVE20, WELCOME50)"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                      required
                      maxLength={20}
                    />
                    <span className="field-hint">Customers will enter this code at checkout</span>
                  </div>

                  {/* Discount Type & Value */}
                  <div className="form-field">
                    <label><FiPercent /> Discount Type</label>
                    <div className="toggle-buttons">
                      <button
                        type="button"
                        className={formData.discountType === 'percentage' ? 'active' : ''}
                        onClick={() => setFormData({ ...formData, discountType: 'percentage' })}
                      >
                        <FiPercent /> Percentage
                      </button>
                      <button
                        type="button"
                        className={formData.discountType === 'fixed' ? 'active' : ''}
                        onClick={() => setFormData({ ...formData, discountType: 'fixed' })}
                      >
                        ₹ Fixed Amount
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label>
                      {formData.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount (₹)'}
                    </label>
                    <div className="input-with-icon">
                      <input
                        type="number"
                        placeholder={formData.discountType === 'percentage' ? 'e.g. 20' : 'e.g. 500'}
                        value={formData.discountValue}
                        onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                        required
                        min="1"
                        max={formData.discountType === 'percentage' ? 100 : undefined}
                      />
                      <span className="input-suffix">{formData.discountType === 'percentage' ? '%' : '₹'}</span>
                    </div>
                  </div>

                  {/* Applicability Section */}
                  <div className="form-field full-width">
                    <label><FiPackage /> Applies To</label>
                    <div className="toggle-buttons applicability-toggle">
                      <button
                        type="button"
                        className={formData.applicableType === 'all' ? 'active' : ''}
                        onClick={() => setFormData({ ...formData, applicableType: 'all', applicableCategories: [], applicableProductIds: [], selectedProducts: [] })}
                      >
                        All Products
                      </button>
                      <button
                        type="button"
                        className={formData.applicableType === 'categories' ? 'active' : ''}
                        onClick={() => setFormData({ ...formData, applicableType: 'categories', applicableProductIds: [], selectedProducts: [] })}
                      >
                        <FiGrid /> Specific Categories
                      </button>
                      <button
                        type="button"
                        className={formData.applicableType === 'products' ? 'active' : ''}
                        onClick={() => setFormData({ ...formData, applicableType: 'products', applicableCategories: [] })}
                      >
                        <FiPackage /> Specific Products
                      </button>
                    </div>
                  </div>

                  {/* Category Selection */}
                  {formData.applicableType === 'categories' && (
                    <div className="form-field full-width">
                      <label>Select Categories</label>
                      <div className="category-chips">
                        {CATEGORIES.map(cat => (
                          <button
                            key={cat.id}
                            type="button"
                            className={`category-chip ${formData.applicableCategories.includes(cat.id) ? 'selected' : ''}`}
                            onClick={() => toggleCategory(cat.id)}
                          >
                            {cat.label}
                            {formData.applicableCategories.includes(cat.id) && <FiX />}
                          </button>
                        ))}
                      </div>
                      <span className="field-hint">
                        {formData.applicableCategories.length === 0
                          ? 'Select at least one category'
                          : `Selected: ${formData.applicableCategories.length} categor${formData.applicableCategories.length === 1 ? 'y' : 'ies'}`}
                      </span>
                    </div>
                  )}

                  {/* Product Selection */}
                  {formData.applicableType === 'products' && (
                    <div className="form-field full-width">
                      <label>Search & Select Products</label>
                      <div className="product-search-wrapper">
                        <FiSearch className="search-icon" />
                        <input
                          type="text"
                          placeholder="Search products by name..."
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                        />
                        {searchLoading && <span className="search-loading">Searching...</span>}
                      </div>

                      {searchResults.length > 0 && (
                        <div className="product-search-results">
                          {searchResults.map(product => (
                            <div
                              key={product.id}
                              className="product-result-item"
                              onClick={() => addProduct(product)}
                            >
                              <img src={product.images?.[0] || product.image} alt={product.name} />
                              <div className="product-result-info">
                                <span className="product-name">{product.name}</span>
                                <span className="product-price">₹{product.price}</span>
                              </div>
                              <FiPlus className="add-icon" />
                            </div>
                          ))}
                        </div>
                      )}

                      {formData.selectedProducts.length > 0 && (
                        <div className="selected-products">
                          <span className="selected-label">Selected Products:</span>
                          <div className="product-chips">
                            {formData.selectedProducts.map(product => (
                              <span key={product.id} className="product-chip">
                                {product.name}
                                <FiX onClick={() => removeProduct(product.id)} />
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <span className="field-hint">
                        {formData.applicableProductIds.length === 0
                          ? 'Search and select at least one product'
                          : `Selected: ${formData.applicableProductIds.length} product${formData.applicableProductIds.length === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  )}

                  {/* Minimum Order Value */}
                  <div className="form-field full-width">
                    <label><FiShoppingCart /> Minimum Order Value</label>
                    <div className="input-with-icon">
                      <span className="input-prefix">₹</span>
                      <input
                        type="number"
                        placeholder="No minimum (leave empty or 0)"
                        value={formData.minOrderValue}
                        onChange={(e) => setFormData({ ...formData, minOrderValue: e.target.value })}
                        min="0"
                        style={{ paddingLeft: '3.5rem' }}
                      />
                    </div>
                    <span className="field-hint">Coupon will only work if cart value is above this amount</span>
                  </div>

                  {/* Validity Period */}
                  <div className="form-field">
                    <label><FiCalendar /> Start Date</label>
                    <input
                      type="date"
                      value={formData.validFrom}
                      onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                      required
                      min={getToday()}
                    />
                  </div>

                  <div className="form-field">
                    <label><FiCalendar /> End Date</label>
                    <input
                      type="date"
                      value={formData.validUntil}
                      onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                      required
                      min={formData.validFrom || getToday()}
                    />
                  </div>

                  {/* Usage Limit */}
                  <div className="form-field full-width">
                    <label><FiUsers /> Usage Limit</label>
                    <input
                      type="number"
                      placeholder="Unlimited (leave empty)"
                      value={formData.usageLimit}
                      onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                      min="1"
                    />
                    <span className="field-hint">Maximum number of times this coupon can be used</span>
                  </div>
                </div>

                {/* Preview */}
                {formData.code && formData.discountValue && (
                  <div className="coupon-preview">
                    <div className="preview-label">Preview</div>
                    <div className="preview-coupon">
                      <span className="preview-code">{formData.code}</span>
                      <span className="preview-discount">
                        {formData.discountType === 'percentage'
                          ? `${formData.discountValue}% OFF`
                          : `₹${formData.discountValue} OFF`}
                      </span>
                      {formData.minOrderValue > 0 && (
                        <span className="preview-condition">on orders above ₹{formData.minOrderValue}</span>
                      )}
                      {formData.applicableType !== 'all' && (
                        <span className="preview-applicability">
                          {formData.applicableType === 'categories'
                            ? `For: ${formData.applicableCategories.join(', ') || 'Select categories'}`
                            : `For: ${formData.applicableProductIds.length} selected product${formData.applicableProductIds.length !== 1 ? 's' : ''}`}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => { setShowForm(false); resetForm(); }}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    <FiPlus /> Create Coupon
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Coupons List */}
          <div className="coupons-list-section">
            <h3>All Coupons ({coupons.length})</h3>
            {loading ? (
              <div className="loading-state">Loading coupons...</div>
            ) : coupons.length === 0 ? (
              <div className="empty-state">
                <FiTag size={48} />
                <h4>No coupons yet</h4>
                <p>Create your first coupon to offer discounts to customers</p>
                <button onClick={() => setShowForm(true)}><FiPlus /> Create Coupon</button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="coupons-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Discount</th>
                      <th>Applies To</th>
                      <th>Min. Order</th>
                      <th>Valid Until</th>
                      <th>Usage</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((coupon) => {
                      const now = new Date();
                      const validFrom = new Date(coupon.validFrom);
                      const validUntil = new Date(coupon.validUntil);
                      const isExpired = now > validUntil;
                      const isUpcoming = now < validFrom;
                      const status = isExpired ? 'expired' : isUpcoming ? 'upcoming' : 'active';

                      return (
                        <tr key={coupon.code} className={status}>
                          <td>
                            <span className="code-badge">{coupon.code}</span>
                          </td>
                          <td>
                            <span className="discount-value">
                              {coupon.discountType === 'percentage'
                                ? `${coupon.discountValue}%`
                                : `₹${coupon.discountValue}`}
                            </span>
                          </td>
                          <td>
                            <span className={`applicability-badge ${coupon.applicableType || 'all'}`}>
                              {getApplicabilityLabel(coupon)}
                            </span>
                          </td>
                          <td>
                            {coupon.minOrderValue > 0
                              ? `₹${coupon.minOrderValue.toLocaleString('en-IN')}`
                              : <span className="text-muted">No minimum</span>}
                          </td>
                          <td>{validUntil.toLocaleDateString('en-IN')}</td>
                          <td>
                            {coupon.usageLimit
                              ? `${coupon.usedCount || 0} / ${coupon.usageLimit}`
                              : <span className="text-muted">Unlimited</span>}
                          </td>
                          <td>
                            <span className={`status-badge ${status}`}>
                              {isExpired ? 'Expired' : isUpcoming ? 'Upcoming' : 'Active'}
                            </span>
                          </td>
                          <td>
                            <button className="delete-btn" onClick={() => handleDelete(coupon.code)} title="Delete">
                              <FiTrash2 />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* BANK OFFERS TAB */}
      {activeTab === 'bankOffers' && (
        <>
          <div className="section-header">
            <h2>Bank Offers</h2>
            <button className="create-coupon-btn" onClick={() => { resetBankOfferForm(); setShowBankOfferForm(!showBankOfferForm); }}>
              {showBankOfferForm ? <><FiX /> Cancel</> : <><FiPlus /> Create Bank Offer</>}
            </button>
          </div>

          {showBankOfferForm && (
            <div className="coupon-form-card bank-offer-form">
              <h3>{editingBankOffer ? 'Edit Bank Offer' : 'Create New Bank Offer'}</h3>
              <form onSubmit={handleBankOfferSubmit}>
                <div className="form-grid">
                  {/* Offer Title */}
                  <div className="form-field full-width">
                    <label><FiCreditCard /> Offer Title *</label>
                    <input
                      type="text"
                      placeholder="e.g., 10% Instant Discount"
                      value={bankOfferFormData.title}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, title: e.target.value })}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="form-field full-width">
                    <label>Description *</label>
                    <textarea
                      className="form-textarea"
                      placeholder="e.g., Get 10% instant discount on HDFC Credit Cards. Maximum discount ₹500."
                      value={bankOfferFormData.description}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, description: e.target.value })}
                      required
                      rows={3}
                    />
                  </div>

                  {/* Bank Name */}
                  <div className="form-field">
                    <label>Bank Name</label>
                    <select
                      value={bankOfferFormData.bankName}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, bankName: e.target.value })}
                    >
                      {BANKS.map(bank => (
                        <option key={bank} value={bank}>{bank}</option>
                      ))}
                    </select>
                  </div>

                  {/* Card Type */}
                  <div className="form-field">
                    <label>Card Type</label>
                    <select
                      value={bankOfferFormData.cardType}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, cardType: e.target.value })}
                    >
                      {CARD_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Discount Type & Value */}
                  <div className="form-field">
                    <label><FiPercent /> Discount Type</label>
                    <div className="toggle-buttons">
                      <button
                        type="button"
                        className={bankOfferFormData.discountType === 'percentage' ? 'active' : ''}
                        onClick={() => setBankOfferFormData({ ...bankOfferFormData, discountType: 'percentage' })}
                      >
                        <FiPercent /> Percentage
                      </button>
                      <button
                        type="button"
                        className={bankOfferFormData.discountType === 'fixed' ? 'active' : ''}
                        onClick={() => setBankOfferFormData({ ...bankOfferFormData, discountType: 'fixed' })}
                      >
                        ₹ Fixed
                      </button>
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Discount Value *</label>
                    <div className="input-with-icon">
                      <input
                        type="number"
                        placeholder={bankOfferFormData.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 500'}
                        value={bankOfferFormData.discountValue}
                        onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, discountValue: e.target.value })}
                        required
                        min="1"
                        max={bankOfferFormData.discountType === 'percentage' ? 100 : undefined}
                      />
                      <span className="input-suffix">{bankOfferFormData.discountType === 'percentage' ? '%' : '₹'}</span>
                    </div>
                  </div>

                  {/* Max Discount (for percentage) */}
                  <div className="form-field">
                    <label>Maximum Discount Cap (₹)</label>
                    <div className="input-with-icon">
                      <span className="input-prefix">₹</span>
                      <input
                        type="number"
                        placeholder="e.g. 500"
                        value={bankOfferFormData.maxDiscount}
                        onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, maxDiscount: e.target.value })}
                        min="0"
                      />
                    </div>
                    <span className="field-hint">Leave empty for no cap</span>
                  </div>

                  {/* Minimum Order Value */}
                  <div className="form-field">
                    <label><FiShoppingCart /> Minimum Order Value</label>
                    <div className="input-with-icon">
                      <span className="input-prefix">₹</span>
                      <input
                        type="number"
                        placeholder="e.g. 2000"
                        value={bankOfferFormData.minOrderValue}
                        onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, minOrderValue: e.target.value })}
                        min="0"
                        style={{ paddingLeft: '3.5rem' }}
                      />
                    </div>
                    <span className="field-hint">Minimum cart value required for this offer</span>
                  </div>

                  {/* BIN Ranges */}
                  <div className="form-field full-width">
                    <label>BIN Ranges (Optional - for payment validation)</label>
                    <input
                      type="text"
                      placeholder="e.g., 411111, 524000-524999"
                      value={bankOfferFormData.applicableBinRanges}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, applicableBinRanges: e.target.value })}
                    />
                    <span className="field-hint">Comma-separated card BIN ranges (first 6 digits). Used for checkout validation.</span>
                  </div>

                  {/* Payment Method Code */}
                  <div className="form-field full-width">
                    <label>Payment Method Code (Optional - for Razorpay)</label>
                    <input
                      type="text"
                      placeholder="e.g., hdfc_cc, icici_dc"
                      value={bankOfferFormData.paymentMethodCode}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, paymentMethodCode: e.target.value })}
                    />
                    <span className="field-hint">Razorpay payment method identifier for automatic validation</span>
                  </div>

                  {/* Validity Period */}
                  <div className="form-field">
                    <label><FiCalendar /> Start Date</label>
                    <input
                      type="date"
                      value={bankOfferFormData.validFrom}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, validFrom: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label><FiCalendar /> End Date</label>
                    <input
                      type="date"
                      value={bankOfferFormData.validUntil}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, validUntil: e.target.value })}
                      required
                      min={bankOfferFormData.validFrom || getToday()}
                    />
                  </div>

                  {/* Terms & Conditions */}
                  <div className="form-field full-width">
                    <label>Terms & Conditions</label>
                    <textarea
                      className="form-textarea"
                      placeholder="Enter terms and conditions for this offer..."
                      value={bankOfferFormData.termsAndConditions}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, termsAndConditions: e.target.value })}
                      rows={4}
                    />
                  </div>

                  {/* Display Order */}
                  <div className="form-field">
                    <label>Display Order</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={bankOfferFormData.displayOrder}
                      onChange={(e) => setBankOfferFormData({ ...bankOfferFormData, displayOrder: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                    <span className="field-hint">Lower numbers appear first</span>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => { setShowBankOfferForm(false); resetBankOfferForm(); }}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    {editingBankOffer ? <><FiEdit2 /> Update Offer</> : <><FiPlus /> Create Offer</>}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Bank Offers List */}
          <div className="coupons-list-section">
            <h3>All Bank Offers ({bankOffers.length})</h3>
            {bankOffersLoading ? (
              <div className="loading-state">Loading bank offers...</div>
            ) : bankOffers.length === 0 ? (
              <div className="empty-state">
                <FiCreditCard size={48} />
                <h4>No bank offers yet</h4>
                <p>Create bank offers to display payment-based discounts to customers</p>
                <button onClick={() => setShowBankOfferForm(true)}><FiPlus /> Create Bank Offer</button>
              </div>
            ) : (
              <div className="bank-offers-grid">
                {bankOffers.map((offer) => {
                  const now = new Date();
                  const validFrom = new Date(offer.validFrom);
                  const validUntil = new Date(offer.validUntil);
                  const isExpired = now > validUntil;
                  const isUpcoming = now < validFrom;
                  const status = isExpired ? 'expired' : isUpcoming ? 'upcoming' : (offer.isActive ? 'active' : 'inactive');

                  return (
                    <div key={offer.id} className={`bank-offer-card ${status}`}>
                      <div className="bank-offer-header">
                        <div className="bank-offer-title-row">
                          <span className="bank-badge">{offer.bankName}</span>
                          <span className={`status-badge ${status}`}>
                            {isExpired ? 'Expired' : isUpcoming ? 'Upcoming' : (offer.isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                        <h4>{offer.title}</h4>
                      </div>
                      <div className="bank-offer-body">
                        <p className="offer-description">{offer.description}</p>
                        <div className="offer-details">
                          <span className="offer-discount">
                            {offer.discountType === 'percentage'
                              ? `${offer.discountValue}% OFF`
                              : `₹${offer.discountValue} OFF`}
                          </span>
                          {offer.maxDiscount && (
                            <span className="offer-max">Max: ₹{offer.maxDiscount}</span>
                          )}
                          {offer.minOrderValue > 0 && (
                            <span className="offer-min">Min Order: ₹{offer.minOrderValue}</span>
                          )}
                        </div>
                        <div className="offer-meta">
                          <span><FiCreditCard /> {offer.cardType}</span>
                          <span><FiCalendar /> Valid till {new Date(offer.validUntil).toLocaleDateString('en-IN')}</span>
                        </div>
                      </div>
                      <div className="bank-offer-actions">
                        <button
                          className={`toggle-btn ${offer.isActive ? 'active' : ''}`}
                          onClick={() => handleToggleBankOffer(offer.id)}
                          title={offer.isActive ? 'Deactivate' : 'Activate'}
                          disabled={isExpired}
                        >
                          {offer.isActive ? <FiToggleRight /> : <FiToggleLeft />}
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => handleEditBankOffer(offer)}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteBankOffer(offer.id)}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCoupons;
